import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Share2, Lightbulb, LayoutGrid, Map, ShieldCheck, ScrollText, Database,
  RefreshCw, Download, Upload, X, AlertTriangle, ExternalLink, Landmark, FileCheck, Eye,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/network`;
const http = axios.create({ withCredentials: true });

const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};

const SUB_TABS = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'participants', label: 'Participants', icon: Users },
  { key: 'referrals', label: 'Referrals', icon: Share2 },
  { key: 'opportunities', label: 'Opportunities', icon: Lightbulb },
  { key: 'funding', label: 'Funding Access', icon: Landmark },
  { key: 'domains', label: 'Domain Coverage', icon: Map },
  { key: 'admins', label: 'Admins', icon: ShieldCheck, superOnly: true },
  { key: 'vetting', label: 'Vetting', icon: FileCheck, superOnly: true },
  { key: 'migration', label: 'Database', icon: Database, superOnly: true },
  { key: 'audit', label: 'Audit Log', icon: ScrollText, superOnly: true },
];

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 p-5">
    <div className={`text-3xl font-bold ${accent || 'text-slate-900'}`}>{value}</div>
    <div className="text-xs text-slate-500 mt-1 leading-snug">{label}</div>
  </div>
);

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-rose-50 text-rose-700',
    blue: 'bg-sky-50 text-sky-700',
  };
  return <span className={`inline-block px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
};

const priorityTone = (p) => (p === 'High' ? 'red' : p === 'Low' ? 'slate' : 'amber');

const Select = ({ value, onChange, options, placeholder, testid, className = '' }) => (
  <select
    value={value}
    onChange={onChange}
    data-testid={testid}
    className={`px-3 py-2 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900 ${className}`}
  >
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Modal = ({ title, onClose, children, testid, wide }) => (
  <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
    <div className={`bg-white w-full ${wide ? 'max-w-5xl' : 'max-w-3xl'} my-8`}>
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 sticky top-0 bg-white">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid={`${testid}-close`}>
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{label}</div>
    {children}
  </div>
);

const ReadOnly = ({ label, value }) => (
  <Field label={label}>
    <div className="text-sm text-slate-800 whitespace-pre-wrap">{value || <span className="text-slate-400">—</span>}</div>
  </Field>
);

const Input = (props) => (
  <input {...props} className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
);
const Area = ({ rows = 3, ...props }) => (
  <textarea rows={rows} {...props} className="w-full px-3 py-2 border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
);

export const NetworkAdmin = ({ admin }) => {
  const isSuper = admin?.role === 'SUPER_ADMIN';
  const [sub, setSub] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState('');
  const [participants, setParticipants] = useState([]);
  const [pMeta, setPMeta] = useState({ roles: [], statuses: [], domains: [], priorities: [] });
  const [pFilters, setPFilters] = useState({ search: '', role: '', domain: '', country: '', status: '' });
  const [referrals, setReferrals] = useState([]);
  const [rMeta, setRMeta] = useState({ statuses: [], priorities: [], domains: [] });
  const [rFilters, setRFilters] = useState({ search: '', status: '', priority: '', domain: '' });
  const [opportunities, setOpportunities] = useState([]);
  const [oMeta, setOMeta] = useState({ statuses: [], priorities: [], types: [], domains: [] });
  const [oFilters, setOFilters] = useState({ search: '', status: '', priority: '', opportunity_type: '' });
  const [coverage, setCoverage] = useState([]);
  const [fundingView, setFundingView] = useState('organisations');
  const [fundingConns, setFundingConns] = useState([]);
  const [fMeta, setFMeta] = useState({ worlds: [], funder_types: {}, depths: [], regions: [], willingness: [], statuses: [], priorities: [] });
  const [fFilters, setFFilters] = useState({ search: '', funding_world: '', status: '', willingness: '', region: '' });
  const [fundingOrgs, setFundingOrgs] = useState([]);
  const [fundingPeople, setFundingPeople] = useState([]);
  const [expandedOrg, setExpandedOrg] = useState('');
  const [reviewFunding, setReviewFunding] = useState(null);
  const [vettingRecords, setVettingRecords] = useState([]);
  const [vMeta, setVMeta] = useState({ statuses: [] });
  const [vFilters, setVFilters] = useState({ search: '', status: '' });
  const [reviewVetting, setReviewVetting] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailForm, setDetailForm] = useState(null);
  const [reviewReferral, setReviewReferral] = useState(null);
  const [reviewOpportunity, setReviewOpportunity] = useState(null);
  const [busy, setBusy] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [newParticipant, setNewParticipant] = useState(null);
  const [migration, setMigration] = useState({ target_uri: '', target_db: 'safe_production' });
  const [migrationResult, setMigrationResult] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const res = await http.get(`${API}/admin/overview`);
      setOverview(res.data);
      setOverviewError('');
    } catch (err) {
      const msg = errMsg(err, 'Could not load the network overview');
      setOverviewError(msg);
      toast.error(msg);
    }
  }, []);

  const loadParticipants = useCallback(async (filters) => {
    try {
      const res = await http.get(`${API}/admin/participants`, { params: filters });
      setParticipants(res.data.participants);
      setPMeta({ roles: res.data.roles, statuses: res.data.statuses, domains: res.data.domains, priorities: res.data.priorities });
    } catch (err) { toast.error(errMsg(err, 'Could not load participants')); }
  }, []);

  const loadReferrals = useCallback(async (filters) => {
    try {
      const res = await http.get(`${API}/admin/referrals`, { params: filters });
      setReferrals(res.data.referrals);
      setRMeta({ statuses: res.data.statuses, priorities: res.data.priorities, domains: res.data.domains });
    } catch (err) { toast.error(errMsg(err, 'Could not load referrals')); }
  }, []);

  const loadOpportunities = useCallback(async (filters) => {
    try {
      const res = await http.get(`${API}/admin/opportunities`, { params: filters });
      setOpportunities(res.data.opportunities);
      setOMeta({ statuses: res.data.statuses, priorities: res.data.priorities, types: res.data.types, domains: res.data.domains });
    } catch (err) { toast.error(errMsg(err, 'Could not load opportunities')); }
  }, []);

  const loadCoverage = useCallback(async () => {
    try {
      const res = await http.get(`${API}/admin/domain-coverage`);
      setCoverage(res.data.coverage);
    } catch (err) { toast.error(errMsg(err, 'Could not load domain coverage')); }
  }, []);

  const loadFunding = useCallback(async (filters) => {
    try {
      const res = await http.get(`${API}/admin/funding-connections`, { params: filters });
      setFundingConns(res.data.connections);
      setFMeta({
        worlds: res.data.worlds, funder_types: res.data.funder_types, depths: res.data.depths,
        regions: res.data.regions, willingness: res.data.willingness,
        statuses: res.data.statuses, priorities: res.data.priorities,
      });
    } catch (err) { toast.error(errMsg(err, 'Could not load funding connections')); }
  }, []);

  const loadFundingOrgs = useCallback(async (world) => {
    try {
      const res = await http.get(`${API}/admin/funding-organisations`, { params: { funding_world: world } });
      setFundingOrgs(res.data.organisations);
    } catch (err) { toast.error(errMsg(err, 'Could not load the funder cross-reference')); }
  }, []);

  const loadFundingPeople = useCallback(async () => {
    try {
      const res = await http.get(`${API}/admin/funding-people`);
      setFundingPeople(res.data.people);
    } catch (err) { toast.error(errMsg(err, 'Could not load funding scores')); }
  }, []);

  const loadVetting = useCallback(async (filters) => {
    try {
      const res = await http.get(`${API}/admin/vetting`, { params: filters });
      setVettingRecords(res.data.records);
      setVMeta({ statuses: res.data.statuses });
    } catch (err) { toast.error(errMsg(err, 'Could not load vetting records')); }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await http.get(`${API}/admin/admins`);
      setAdmins(res.data.admins);
    } catch (err) { toast.error(errMsg(err, 'Could not load admins')); }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const res = await http.get(`${API}/admin/audit-log`, { params: { limit: 300 } });
      setAuditEntries(res.data.entries);
    } catch (err) { toast.error(errMsg(err, 'Could not load the audit log')); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (sub === 'participants') loadParticipants(pFilters); }, [sub, pFilters, loadParticipants]);
  useEffect(() => { if (sub === 'referrals') loadReferrals(rFilters); }, [sub, rFilters, loadReferrals]);
  useEffect(() => { if (sub === 'opportunities') loadOpportunities(oFilters); }, [sub, oFilters, loadOpportunities]);
  useEffect(() => { if (sub === 'domains') loadCoverage(); }, [sub, loadCoverage]);
  useEffect(() => {
    if (sub !== 'funding') return;
    // Always load the connections — the option metadata and the records behind the
    // Review links in the other two views both come from this call.
    loadFunding(fundingView === 'connections' ? fFilters : { funding_world: fFilters.funding_world });
    if (fundingView === 'organisations') loadFundingOrgs(fFilters.funding_world);
    if (fundingView === 'people') loadFundingPeople();
  }, [sub, fundingView, fFilters, loadFunding, loadFundingOrgs, loadFundingPeople]);
  useEffect(() => { if (sub === 'admins') loadAdmins(); }, [sub, loadAdmins]);
  useEffect(() => { if (sub === 'vetting') loadVetting(vFilters); }, [sub, vFilters, loadVetting]);
  useEffect(() => { if (sub === 'audit') loadAudit(); }, [sub, loadAudit]);

  const openParticipant = async (id) => {
    try {
      const res = await http.get(`${API}/admin/participants/${id}`);
      setDetail(res.data);
      setDetailForm({
        full_name: res.data.full_name || '',
        safe_role: res.data.safe_role || 'Participant',
        role_title: res.data.role_title || '',
        organisations: res.data.organisations || '',
        country: res.data.country || '',
        primary_domain: res.data.primary_domain || '',
        participant_status: res.data.participant_status || 'Invited',
        priority: res.data.priority || 'Medium',
        assigned_owner: res.data.assigned_owner || '',
        internal_notes: res.data.internal_notes || '',
        unmatched: !!res.data.unmatched,
        snapshot: {
          who_they_are: res.data.snapshot?.who_they_are || '',
          what_they_are_building: res.data.snapshot?.what_they_are_building || '',
          what_safe_can_do_for_them: res.data.snapshot?.what_safe_can_do_for_them || '',
          what_they_can_do_for_safe: res.data.snapshot?.what_they_can_do_for_safe || '',
          key_relationships: res.data.snapshot?.key_relationships || '',
          potential_collaborations: res.data.snapshot?.potential_collaborations || '',
          follow_up_actions: res.data.snapshot?.follow_up_actions || '',
        },
      });
    } catch (err) { toast.error(errMsg(err, 'Could not open that participant')); }
  };

  const saveParticipant = async () => {
    setBusy('participant');
    try {
      await http.put(`${API}/admin/participants/${detail.participant_id}`, detailForm);
      toast.success('Participant updated.');
      setDetail(null);
      loadParticipants(pFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not save those changes')); }
    finally { setBusy(''); }
  };

  const deleteParticipant = async () => {
    if (!window.confirm(`Remove ${detail.full_name || detail.email} from the Council Network? Their referrals and opportunities are kept, but will need re-attributing.`)) return;
    setBusy('delete');
    try {
      const res = await http.delete(`${API}/admin/participants/${detail.participant_id}`);
      toast.success(`Removed. ${res.data.detached_referrals} referral(s) and ${res.data.detached_opportunities} opportunity(ies) kept and flagged for re-attribution.`);
      setDetail(null);
      loadParticipants(pFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not remove that participant')); }
    finally { setBusy(''); }
  };

  const syncHubspot = async (id) => {
    setBusy('hubspot');
    try {
      const res = await http.post(`${API}/admin/participants/${id}/hubspot-sync`);
      toast.success(`Synced to HubSpot (contact ${res.data.hubspot_contact_id}).`);
      loadParticipants(pFilters);
    } catch (err) { toast.error(errMsg(err, 'HubSpot sync failed')); }
    finally { setBusy(''); }
  };

  const saveReferral = async () => {
    setBusy('referral');
    try {
      const { status, priority, domain, assigned_owner, admin_notes } = reviewReferral;
      await http.put(`${API}/admin/referrals/${reviewReferral.referral_id}`,
        { status, priority, domain, assigned_owner, admin_notes });
      toast.success('Referral updated.');
      setReviewReferral(null);
      loadReferrals(rFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not save the referral')); }
    finally { setBusy(''); }
  };

  const promoteReferral = async () => {
    setBusy('promote');
    try {
      const res = await http.post(`${API}/admin/referrals/${reviewReferral.referral_id}/hubspot-contact`);
      toast.success(`HubSpot contact created (${res.data.hubspot_contact_id}).`);
      setReviewReferral(null);
      loadReferrals(rFilters);
    } catch (err) { toast.error(errMsg(err, 'Could not create the HubSpot contact')); }
    finally { setBusy(''); }
  };

  const saveOpportunity = async () => {
    setBusy('opportunity');
    try {
      const { status, priority, domain, opportunity_type, deadline, assigned_owner, admin_notes } = reviewOpportunity;
      await http.put(`${API}/admin/opportunities/${reviewOpportunity.opportunity_id}`,
        { status, priority, domain, opportunity_type, deadline, assigned_owner, admin_notes });
      toast.success('Opportunity updated.');
      setReviewOpportunity(null);
      loadOpportunities(oFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not save the opportunity')); }
    finally { setBusy(''); }
  };

  const saveFundingConnection = async () => {
    setBusy('funding');
    try {
      const { status, priority, assigned_owner, admin_notes, verified, access_depth, funder_type } = reviewFunding;
      await http.put(`${API}/admin/funding-connections/${reviewFunding.connection_id}`,
        { status, priority, assigned_owner, admin_notes, verified, access_depth, funder_type });
      toast.success('Funding connection updated.');
      setReviewFunding(null);
      if (fundingView === 'connections') loadFunding(fFilters);
      else {
        loadFunding({ funding_world: fFilters.funding_world });
        if (fundingView === 'organisations') loadFundingOrgs(fFilters.funding_world);
        else loadFundingPeople();
      }
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not save the funding connection')); }
    finally { setBusy(''); }
  };

  const recomputeFunding = async () => {
    setBusy('rescore');
    try {
      const res = await http.post(`${API}/admin/funding-recompute`);
      toast.success(`Re-scored ${res.data.participants_scored} participant(s).`);
      loadFundingPeople();
    } catch (err) { toast.error(errMsg(err, 'Could not re-score')); }
    finally { setBusy(''); }
  };

  const openVettingDocument = async (recordId, kind, label) => {
    setBusy(`doc-${kind}`);
    try {
      const res = await http.get(`${API}/admin/vetting/${recordId}/file/${kind}`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      setDocPreview({ url, kind, label, contentType: res.data.type });
    } catch (err) { toast.error(errMsg(err, 'Could not open that document')); }
    finally { setBusy(''); }
  };

  const closeDocPreview = () => {
    if (docPreview?.url) window.URL.revokeObjectURL(docPreview.url);
    setDocPreview(null);
  };

  const saveVetting = async () => {
    setBusy('vetting');
    try {
      const { status, admin_notes } = reviewVetting;
      await http.put(`${API}/admin/vetting/${reviewVetting.record_id}`, { status, admin_notes });
      toast.success('Vetting record updated.');
      setReviewVetting(null);
      loadVetting(vFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not save the vetting record')); }
    finally { setBusy(''); }
  };

  const saveDomainLead = async (row) => {
    try {
      await http.put(`${API}/admin/domain-coverage`, {
        domain: row.domain, council_lead: row.council_lead, notes: row.notes || '',
      });
      toast.success(`${row.domain} lead saved.`);
      loadCoverage();
    } catch (err) { toast.error(errMsg(err, 'Could not save the domain lead')); }
  };

  const importCouncil = async (includePending = false) => {
    setBusy('import');
    try {
      const res = await http.post(`${API}/admin/import-council`, null,
        { params: { include_pending: includePending } });
      const d = res.data;
      setImportResult({ kind: 'applications', ...d });
      if (d.created + d.updated === 0) {
        toast.warning(d.scanned === 0
          ? 'There are no Council onboarding applications in the database to import.'
          : `Scanned ${d.scanned} application(s) but imported none — see the breakdown below.`);
      } else {
        toast.success(`Imported: ${d.created} new, ${d.updated} updated.`);
      }
      loadParticipants(pFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Council import failed')); }
    finally { setBusy(''); }
  };

  const importRoster = async () => {
    setBusy('roster');
    try {
      const res = await http.post(`${API}/admin/import-roster`);
      const d = res.data;
      setImportResult({ kind: 'roster', ...d });
      if (d.created + d.updated === 0) {
        toast.warning('No roster members have an email address yet, so nothing could be imported.');
      } else {
        toast.success(`Council page roster: ${d.created} new, ${d.updated} updated.`);
      }
      loadParticipants(pFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Roster import failed')); }
    finally { setBusy(''); }
  };

  const exportCsv = async (kind) => {
    try {
      const res = await http.get(`${API}/admin/export/${kind}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `safe_network_${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded — this has been recorded in the audit log.');
    } catch (err) { toast.error(errMsg(err, 'Export failed or is not permitted')); }
  };

  const createParticipant = async () => {
    setBusy('create');
    try {
      await http.post(`${API}/admin/participants`, newParticipant);
      toast.success('Participant added.');
      setNewParticipant(null);
      loadParticipants(pFilters);
      loadOverview();
    } catch (err) { toast.error(errMsg(err, 'Could not add that participant')); }
    finally { setBusy(''); }
  };

  const updateAdminRole = async (userId, patch) => {
    try {
      await http.put(`${API}/admin/admins/${userId}`, patch);
      toast.success('Admin permissions updated.');
      loadAdmins();
    } catch (err) { toast.error(errMsg(err, 'Could not change those permissions')); }
  };

  const runMigration = async (step) => {
    if (!migration.target_uri.trim() || !migration.target_db.trim()) {
      toast.error('Paste the connection string and the target database name first.');
      return;
    }
    if (step === 'copy' && !window.confirm(
      `Copy every collection into "${migration.target_db}"? Documents that already exist are skipped, so this is safe to run more than once.`)) return;
    setBusy(`migration-${step}`);
    try {
      const res = await http.post(`${API}/admin/migration/${step}`, migration);
      setMigrationResult({ step, ...res.data });
      if (step === 'inspect') {
        toast.success(res.data.writable
          ? `Connected to ${res.data.target_host}. Ready to copy ${res.data.total_source_documents} documents.`
          : `Connected to ${res.data.target_host}, but it is NOT writable — check the database user's permissions.`);
      } else if (step === 'copy') {
        toast.success(`Copied ${res.data.total_copied} documents (${res.data.total_already_present} were already there).`);
      } else {
        toast[res.data.all_match ? 'success' : 'error'](res.data.all_match
          ? 'Every collection matches. Safe to switch MONGO_URL.'
          : 'Some collections are short — see the table before switching over.');
      }
    } catch (err) { toast.error(errMsg(err, 'The migration step failed')); }
    finally { setBusy(''); }
  };

  const visibleTabs = SUB_TABS.filter((t) => !t.superOnly || isSuper);

  return (
    <div data-testid="network-admin">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 mb-8">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setSub(t.key)}
            data-testid={`network-subtab-${t.key}`}
            className={`flex items-center gap-2 text-sm font-medium px-4 py-3 border-b-2 -mb-px transition-colors ${
              sub === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.75} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ---------------- Overview ---------------- */}
      {sub === 'overview' && overviewError && (
        <div className="border-l-2 border-rose-500 bg-rose-50 p-6" data-testid="network-overview-error">
          <h3 className="text-sm font-bold text-rose-900 mb-1">The overview could not be loaded</h3>
          <p className="text-sm text-rose-800 leading-relaxed">{overviewError}</p>
          <button onClick={loadOverview} className="btn-primary mt-4" data-testid="network-overview-retry">Try again</button>
        </div>
      )}

      {sub === 'overview' && !overview && !overviewError && (
        <div className="text-sm text-slate-500" data-testid="network-overview-loading">Loading the Council Network…</div>
      )}

      {sub === 'overview' && overview && (
        <div className="space-y-8" data-testid="network-overview">
          {overview.total_participants === 0 && (
            <div className="bg-slate-900 text-white p-8" data-testid="network-empty-state">
              <h3 className="text-lg font-bold mb-3">Start by loading your Council members</h3>
              <p className="text-sm text-slate-300 max-w-2xl leading-relaxed mb-6">
                There are no participants yet. Council onboarding applications now mirror
                into this list automatically, so this is only needed for older records —
                it is safe to run as often as you like.
              </p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => importCouncil(false)} disabled={busy === 'import'}
                  data-testid="network-import-council-empty"
                  className="inline-flex items-center gap-2 text-sm font-bold px-5 py-3 bg-white text-slate-900 hover:bg-slate-100 transition-colors">
                  <Upload className="w-4 h-4" strokeWidth={2} />
                  {busy === 'import' ? 'Importing…' : 'Import approved Council members'}
                </button>
                <button onClick={() => importCouncil(true)} disabled={busy === 'import'}
                  data-testid="network-import-council-pending"
                  className="text-sm font-medium px-5 py-3 border border-slate-600 text-slate-200 hover:border-white hover:text-white transition-colors">
                  Include pending applications
                </button>
                <button onClick={importRoster} disabled={busy === 'roster'}
                  data-testid="network-import-roster-empty"
                  className="text-sm font-medium px-5 py-3 border border-slate-600 text-slate-200 hover:border-white hover:text-white transition-colors">
                  {busy === 'roster' ? 'Importing…' : 'Import public Council page roster'}
                </button>
                <button onClick={() => { setSub('participants'); setNewParticipant({ full_name: '', email: '', safe_role: 'Council Member', role_title: '', organisations: '', country: '', primary_domain: '' }); }}
                  data-testid="network-empty-add-manual"
                  className="text-sm font-medium px-5 py-3 border border-slate-600 text-slate-200 hover:border-white hover:text-white transition-colors">
                  Add someone manually instead
                </button>
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <Stat label="Total participants" value={overview.total_participants} />
            <Stat label="Council members" value={overview.total_council_members} />
            <Stat label="Advisors" value={overview.total_advisors} />
            <Stat label="Profiles submitted" value={overview.profiles_submitted} />
            <Stat label="Countries represented" value={overview.countries_represented} />
            <Stat label="Total referrals" value={overview.total_referrals} />
            <Stat label="High-priority referrals" value={overview.high_priority_referrals} accent="text-rose-600" />
            <Stat label="Total opportunities" value={overview.total_opportunities} />
            <Stat label="High-priority opportunities" value={overview.high_priority_opportunities} accent="text-rose-600" />
            <Stat label="Introductions offered" value={overview.introductions_offered} accent="text-emerald-600" />
            <Stat label="Funding connections" value={overview.funding_connections ?? 0} />
            <Stat label="Commercial investors reachable" value={overview.funding_commercial ?? 0} />
            <Stat label="Philanthropic funders reachable" value={overview.funding_philanthropic ?? 0} />
            <Stat label="Decision-maker level access" value={overview.funding_decision_maker_access ?? 0} accent="text-emerald-600" />
            <Stat label="Direct introductions offered to funders" value={overview.funding_direct_introductions ?? 0} accent="text-emerald-600" />
            <Stat label="Police checks submitted" value={overview.vetting_submitted ?? 0} />
            <Stat label="Identities verified" value={overview.vetting_verified ?? 0} accent="text-emerald-600" />
            <Stat label="Vetting awaiting review" value={overview.vetting_awaiting_review ?? 0} accent="text-amber-600" />
          </div>

          {overview.unmatched_submissions > 0 && (
            <div className="flex items-start gap-3 border-l-2 border-amber-500 bg-amber-50 p-5" data-testid="network-unmatched-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="text-sm text-amber-900">
                <strong>{overview.unmatched_submissions}</strong> submission
                {overview.unmatched_submissions === 1 ? '' : 's'} could not be matched to a known
                Council record by email address. Open the relevant tab and attribute them.
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">Upcoming opportunity deadlines (next 90 days)</h3>
              <button onClick={loadOverview} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid="network-overview-refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {overview.upcoming_deadlines.length === 0 ? (
              <div className="px-6 py-8 text-sm text-slate-500">No deadlines recorded in the next 90 days.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {overview.upcoming_deadlines.map((d) => (
                  <div key={d.opportunity_id} className="px-6 py-4 flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{d.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{d.organisation || 'No organisation recorded'}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Pill tone={priorityTone(d.priority)}>{d.priority}</Pill>
                      <span className="text-sm font-mono text-slate-700">{d.deadline}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 p-6">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Council data</h3>
            <p className="text-sm text-slate-600 mb-4 max-w-2xl leading-relaxed">
              Two sources. <strong>Onboarding applications</strong> are people who applied through
              /onboarding. The <strong>public Council page roster</strong> is everyone published on
              /council who never submitted an application. Both are safe to run repeatedly.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => importCouncil(false)} disabled={busy === 'import'} className="btn-primary inline-flex items-center gap-2" data-testid="network-import-council">
                <Upload className="w-4 h-4" strokeWidth={1.75} />
                {busy === 'import' ? 'Importing…' : 'Import approved applications'}
              </button>
              <button onClick={() => importCouncil(true)} disabled={busy === 'import'} data-testid="network-import-council-pending-overview"
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                Include pending
              </button>
              <button onClick={importRoster} disabled={busy === 'roster'} data-testid="network-import-roster"
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                <Upload className="w-4 h-4" strokeWidth={1.75} />
                {busy === 'roster' ? 'Importing…' : 'Import public Council page roster'}
              </button>
              {isSuper && ['participants', 'referrals', 'opportunities', 'funding'].map((k) => (
                <button key={k} onClick={() => exportCsv(k)} data-testid={`network-export-${k}`}
                  className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                  <Download className="w-4 h-4" strokeWidth={1.75} />
                  Export {k}
                </button>
              ))}
            </div>
            {!isSuper && (
              <p className="text-xs text-slate-500 mt-4">
                Bulk CSV export is restricted to Super Admins.
              </p>
            )}

            {importResult && (
              <div className="mt-6 border-t border-slate-200 pt-5" data-testid="network-import-result">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-slate-900">
                    {importResult.kind === 'roster' ? 'Council page roster' : 'Onboarding applications'} — last import
                  </h4>
                  <button onClick={() => setImportResult(null)} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid="network-import-result-close">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-6 text-sm mb-4">
                  {importResult.scanned !== undefined && (
                    <span className="text-slate-600">Scanned <strong className="text-slate-900">{importResult.scanned}</strong></span>
                  )}
                  {importResult.roster_size !== undefined && (
                    <span className="text-slate-600">Roster size <strong className="text-slate-900">{importResult.roster_size}</strong></span>
                  )}
                  <span className="text-slate-600">Added <strong className="text-emerald-700">{importResult.created}</strong></span>
                  <span className="text-slate-600">Updated <strong className="text-slate-900">{importResult.updated}</strong></span>
                </div>
                {[
                  ['skipped_status', 'Skipped — status not included in this import'],
                  ['skipped_no_email', 'Skipped — no email address on the application'],
                  ['duplicate_emails', 'Skipped — this email already appeared earlier in the list, so the first record was kept'],
                  ['missing_email', 'Skipped — no email address recorded for this person yet'],
                  ['collisions', 'Needs your attention — more than one existing record shares these addresses, so nothing was changed'],
                ].map(([key, label]) => (
                  (importResult[key] || []).length > 0 && (
                    <div key={key} className="mb-3">
                      <div className="text-xs font-semibold text-amber-800 mb-1">
                        {label} ({importResult[key].length})
                      </div>
                      <div className="text-sm text-slate-700 leading-relaxed">
                        {importResult[key].join(' · ')}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- Participants ---------------- */}
      {sub === 'participants' && (
        <div className="space-y-6" data-testid="network-participants">
          <div className="flex flex-wrap gap-3 items-center">
            <Input placeholder="Search name, email, organisation…" value={pFilters.search}
              onChange={(e) => setPFilters({ ...pFilters, search: e.target.value })}
              data-testid="network-participants-search" style={{ maxWidth: '20rem' }} />
            <Select value={pFilters.role} onChange={(e) => setPFilters({ ...pFilters, role: e.target.value })}
              options={pMeta.roles} placeholder="All roles" testid="network-participants-role" />
            <Select value={pFilters.domain} onChange={(e) => setPFilters({ ...pFilters, domain: e.target.value })}
              options={pMeta.domains} placeholder="All domains" testid="network-participants-domain" />
            <Select value={pFilters.status} onChange={(e) => setPFilters({ ...pFilters, status: e.target.value })}
              options={pMeta.statuses} placeholder="All statuses" testid="network-participants-status" />
            <Input placeholder="Country" value={pFilters.country}
              onChange={(e) => setPFilters({ ...pFilters, country: e.target.value })}
              data-testid="network-participants-country" style={{ maxWidth: '10rem' }} />
            <div className="ml-auto flex gap-3">
              <button onClick={() => importCouncil(false)} disabled={busy === 'import'} data-testid="network-import-council-participants"
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                <Upload className="w-4 h-4" strokeWidth={1.75} />
                {busy === 'import' ? 'Importing…' : 'Import Council members'}
              </button>
              <button onClick={() => setNewParticipant({ full_name: '', email: '', safe_role: 'Council Member', role_title: '', organisations: '', country: '', primary_domain: '' })}
                className="btn-primary" data-testid="network-add-participant">Add participant</button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="text-left px-4 py-3 font-semibold">Organisation</th>
                  <th className="text-left px-4 py-3 font-semibold">Country</th>
                  <th className="text-left px-4 py-3 font-semibold">Primary domain</th>
                  <th className="text-left px-4 py-3 font-semibold">Profile</th>
                  <th className="text-left px-4 py-3 font-semibold">Intro</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {participants.map((p) => (
                  <tr key={p.participant_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{p.full_name || '—'}</div>
                      <div className="text-xs text-slate-500">{p.email}</div>
                      {p.email_missing && <div className="mt-1" data-testid={`network-email-missing-${p.participant_id}`}><Pill tone="amber">Email missing — add before they can submit</Pill></div>}
                      {p.council_application_status && (
                        <div className="mt-1 text-xs text-slate-400">Council record: {p.council_application_status}</div>
                      )}
                      {p.unmatched && <div className="mt-1"><Pill tone="amber">Needs attribution</Pill></div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{p.safe_role}</td>
                    <td className="px-4 py-3 text-slate-700">{p.organisations || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{p.country || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{p.primary_domain || '—'}</td>
                    <td className="px-4 py-3">
                      <Pill tone={p.profile_status === 'submitted' ? 'green' : 'slate'}>
                        {p.profile_status === 'submitted' ? 'Submitted' : 'Not started'}
                      </Pill>
                    </td>
                    <td className="px-4 py-3">
                      {p.intro_willingness ? <Pill tone={p.intro_willingness === 'Yes' ? 'green' : 'slate'}>{p.intro_willingness}</Pill> : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openParticipant(p.participant_id)}
                        data-testid={`network-open-participant-${p.participant_id}`}
                        className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
                {participants.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">No participants match those filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- Referrals ---------------- */}
      {sub === 'referrals' && (
        <div className="space-y-6" data-testid="network-referrals">
          <div className="flex flex-wrap gap-3 items-center">
            <Input placeholder="Search person, organisation, referrer…" value={rFilters.search}
              onChange={(e) => setRFilters({ ...rFilters, search: e.target.value })}
              data-testid="network-referrals-search" style={{ maxWidth: '20rem' }} />
            <Select value={rFilters.status} onChange={(e) => setRFilters({ ...rFilters, status: e.target.value })}
              options={rMeta.statuses} placeholder="All statuses" testid="network-referrals-status" />
            <Select value={rFilters.priority} onChange={(e) => setRFilters({ ...rFilters, priority: e.target.value })}
              options={rMeta.priorities} placeholder="All priorities" testid="network-referrals-priority" />
            <Select value={rFilters.domain} onChange={(e) => setRFilters({ ...rFilters, domain: e.target.value })}
              options={rMeta.domains} placeholder="All domains" testid="network-referrals-domain" />
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Referred person</th>
                  <th className="text-left px-4 py-3 font-semibold">Organisation</th>
                  <th className="text-left px-4 py-3 font-semibold">Referred by</th>
                  <th className="text-left px-4 py-3 font-semibold">Domain</th>
                  <th className="text-left px-4 py-3 font-semibold">Intro</th>
                  <th className="text-left px-4 py-3 font-semibold">Priority</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {referrals.map((r) => (
                  <tr key={r.referral_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{r.person_name}</div>
                      <div className="text-xs text-slate-500">{r.person_title || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.organisation || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{r.referrer_display || r.referrer_email}</div>
                      {r.unmatched && <div className="mt-1"><Pill tone="amber">Unmatched referrer</Pill></div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.domain || '—'}</td>
                    <td className="px-4 py-3">{r.introduction_available ? <Pill tone={r.introduction_available === 'Yes' ? 'green' : 'slate'}>{r.introduction_available}</Pill> : '—'}</td>
                    <td className="px-4 py-3"><Pill tone={priorityTone(r.priority)}>{r.priority}</Pill></td>
                    <td className="px-4 py-3"><Pill tone={r.status === 'Engaged' ? 'green' : r.status === 'New' ? 'blue' : 'slate'}>{r.status}</Pill></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setReviewReferral({ ...r })}
                        data-testid={`network-open-referral-${r.referral_id}`}
                        className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">Review</button>
                    </td>
                  </tr>
                ))}
                {referrals.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-500">No referrals match those filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- Opportunities ---------------- */}
      {sub === 'opportunities' && (
        <div className="space-y-6" data-testid="network-opportunities">
          <div className="flex flex-wrap gap-3 items-center">
            <Input placeholder="Search title, organisation, submitter…" value={oFilters.search}
              onChange={(e) => setOFilters({ ...oFilters, search: e.target.value })}
              data-testid="network-opportunities-search" style={{ maxWidth: '20rem' }} />
            <Select value={oFilters.status} onChange={(e) => setOFilters({ ...oFilters, status: e.target.value })}
              options={oMeta.statuses} placeholder="All statuses" testid="network-opportunities-status" />
            <Select value={oFilters.priority} onChange={(e) => setOFilters({ ...oFilters, priority: e.target.value })}
              options={oMeta.priorities} placeholder="All priorities" testid="network-opportunities-priority" />
            <Select value={oFilters.opportunity_type} onChange={(e) => setOFilters({ ...oFilters, opportunity_type: e.target.value })}
              options={oMeta.types} placeholder="All types" testid="network-opportunities-type" />
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Opportunity</th>
                  <th className="text-left px-4 py-3 font-semibold">Type</th>
                  <th className="text-left px-4 py-3 font-semibold">Submitted by</th>
                  <th className="text-left px-4 py-3 font-semibold">Deadline</th>
                  <th className="text-left px-4 py-3 font-semibold">Priority</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {opportunities.map((o) => (
                  <tr key={o.opportunity_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{o.title}</div>
                      <div className="text-xs text-slate-500">{o.organisation || '—'}{o.country ? ` · ${o.country}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{o.opportunity_type || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{o.submitter_display || o.submitter_email}</div>
                      {o.unmatched && <div className="mt-1"><Pill tone="amber">Unmatched</Pill></div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-700">{o.deadline || '—'}</td>
                    <td className="px-4 py-3"><Pill tone={priorityTone(o.priority)}>{o.priority}</Pill></td>
                    <td className="px-4 py-3"><Pill tone={o.status === 'Won' ? 'green' : o.status === 'New' ? 'blue' : 'slate'}>{o.status}</Pill></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setReviewOpportunity({ ...o })}
                        data-testid={`network-open-opportunity-${o.opportunity_id}`}
                        className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">Review</button>
                    </td>
                  </tr>
                ))}
                {opportunities.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">No opportunities match those filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- Funding access ---------------- */}
      {sub === 'funding' && (
        <div className="space-y-6" data-testid="network-funding">
          <div className="border-l-2 border-slate-900 bg-white p-6">
            <p className="text-sm text-slate-700 leading-relaxed">
              Commercial investment and philanthropic funding are scored <strong>separately</strong> —
              they behave nothing alike, so nothing is averaged. Depth of access carries the most
              weight (55), then how many funders a person can reach (20), the size of cheque they
              operate around (15), and what they are actually willing to do (10). Use{' '}
              <strong>By organisation</strong> to see everyone who can reach the same funder before
              anybody is approached.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {[
              ['organisations', 'By organisation'],
              ['people', 'By person'],
              ['connections', 'All connections'],
            ].map(([key, label]) => (
              <button key={key} onClick={() => setFundingView(key)}
                data-testid={`network-funding-view-${key}`}
                className={`text-sm font-medium px-4 py-2 border transition-colors ${
                  fundingView === key ? 'bg-slate-900 text-white border-slate-900'
                    : 'border-slate-300 text-slate-600 hover:border-slate-900'}`}>
                {label}
              </button>
            ))}
            <div className="flex-1" />
            {fundingView === 'people' && (
              <button onClick={recomputeFunding} disabled={busy === 'rescore'}
                data-testid="network-funding-rescore"
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 border border-slate-300 hover:border-slate-900 transition-colors">
                <RefreshCw className="w-4 h-4" strokeWidth={1.75} />
                {busy === 'rescore' ? 'Re-scoring…' : 'Re-score everyone'}
              </button>
            )}
          </div>

          {fundingView !== 'people' && (
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={fFilters.funding_world}
                onChange={(e) => setFFilters({ ...fFilters, funding_world: e.target.value })}
                options={fMeta.worlds} placeholder="Both funding worlds"
                testid="network-funding-world" />
              {fundingView === 'connections' && (
                <>
                  <Input placeholder="Search organisation, contact, submitter…" value={fFilters.search}
                    onChange={(e) => setFFilters({ ...fFilters, search: e.target.value })}
                    data-testid="network-funding-search" style={{ maxWidth: '20rem' }} />
                  <Select value={fFilters.status} onChange={(e) => setFFilters({ ...fFilters, status: e.target.value })}
                    options={fMeta.statuses} placeholder="All statuses" testid="network-funding-status" />
                  <Select value={fFilters.willingness} onChange={(e) => setFFilters({ ...fFilters, willingness: e.target.value })}
                    options={fMeta.willingness} placeholder="Any willingness" testid="network-funding-willingness" />
                  <Select value={fFilters.region} onChange={(e) => setFFilters({ ...fFilters, region: e.target.value })}
                    options={fMeta.regions} placeholder="Any region" testid="network-funding-region" />
                </>
              )}
            </div>
          )}

          {/* By organisation — the cross-reference */}
          {fundingView === 'organisations' && (
            <div className="space-y-3" data-testid="network-funding-orgs">
              {fundingOrgs.map((org) => (
                <div key={org.organisation_key} className="bg-white border border-slate-200">
                  <button onClick={() => setExpandedOrg(expandedOrg === org.organisation_key ? '' : org.organisation_key)}
                    data-testid={`network-funding-org-${org.organisation_key.replace(/\s+/g, '-')}`}
                    className="w-full text-left px-5 py-4 flex flex-wrap items-center gap-3 hover:bg-slate-50 transition-colors">
                    <div className="flex-1 min-w-[14rem]">
                      <div className="font-semibold text-slate-900">{org.organisation}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {org.funding_world}{org.funder_types.length ? ` · ${org.funder_types.join(', ')}` : ''}
                      </div>
                    </div>
                    <Pill tone={org.people_count > 1 ? 'blue' : 'slate'}>
                      {org.people_count} {org.people_count === 1 ? 'person' : 'people'} can reach
                    </Pill>
                    <Pill tone={org.best_depth_score >= 3 ? 'green' : org.best_depth_score >= 2 ? 'amber' : 'slate'}>
                      Best access: {org.best_depth || '—'}
                    </Pill>
                    {org.direct_introductions > 0 && (
                      <Pill tone="green">{org.direct_introductions} direct intro offered</Pill>
                    )}
                  </button>
                  {expandedOrg === org.organisation_key && (
                    <div className="border-t border-slate-200 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold">Council member</th>
                            <th className="text-left px-4 py-3 font-semibold">Their contact there</th>
                            <th className="text-left px-4 py-3 font-semibold">Depth of access</th>
                            <th className="text-left px-4 py-3 font-semibold">Willing to</th>
                            <th className="text-left px-4 py-3 font-semibold">Ticket</th>
                            <th className="px-4 py-3"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {org.people.map((p) => (
                            <tr key={p.connection_id}>
                              <td className="px-4 py-3">
                                <div className="font-medium text-slate-900">{p.name}</div>
                                <div className="text-xs text-slate-500">{p.email}</div>
                                {p.unmatched && <div className="mt-1"><Pill tone="amber">Unmatched</Pill></div>}
                              </td>
                              <td className="px-4 py-3 text-slate-700">
                                {p.contact_name || '—'}
                                {p.contact_role && <div className="text-xs text-slate-500">{p.contact_role}</div>}
                              </td>
                              <td className="px-4 py-3">
                                <Pill tone={p.access_depth_score >= 3 ? 'green' : p.access_depth_score >= 2 ? 'amber' : 'slate'}>
                                  {p.access_depth_score}/4
                                </Pill>
                                <div className="text-xs text-slate-600 mt-1 max-w-xs">{p.access_depth || '—'}</div>
                              </td>
                              <td className="px-4 py-3 text-slate-700 text-xs max-w-xs">{p.willingness || '—'}</td>
                              <td className="px-4 py-3 text-slate-700 text-xs">{p.typical_ticket || '—'}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => {
                                    const conn = fundingConns.find((c) => c.connection_id === p.connection_id);
                                    if (conn) setReviewFunding({ ...conn });
                                    else toast.error('Could not open that connection — refresh and try again.');
                                  }}
                                  data-testid={`network-funding-open-${p.connection_id}`}
                                  className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">
                                  Review
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
              {fundingOrgs.length === 0 && (
                <div className="bg-white border border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                  No funding connections have been submitted yet.
                </div>
              )}
            </div>
          )}

          {/* By person — two scores, never combined */}
          {fundingView === 'people' && (
            <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="network-funding-people">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Participant</th>
                    <th className="text-left px-4 py-3 font-semibold">Role</th>
                    <th className="text-center px-4 py-3 font-semibold">Commercial</th>
                    <th className="text-center px-4 py-3 font-semibold">Philanthropic</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {fundingPeople.map((p) => (
                    <tr key={p.participant_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{p.full_name || p.email}</div>
                        <div className="text-xs text-slate-500">{p.organisations || p.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{p.safe_role || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-lg font-bold text-slate-900">{p.commercial_score}</div>
                        <Pill tone={p.commercial_score >= 70 ? 'green' : p.commercial_score >= 40 ? 'amber' : 'slate'}>
                          {p.commercial_band}
                        </Pill>
                        <div className="text-xs text-slate-500 mt-1">{p.commercial_count} funder(s)</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-lg font-bold text-slate-900">{p.philanthropic_score}</div>
                        <Pill tone={p.philanthropic_score >= 70 ? 'green' : p.philanthropic_score >= 40 ? 'amber' : 'slate'}>
                          {p.philanthropic_band}
                        </Pill>
                        <div className="text-xs text-slate-500 mt-1">{p.philanthropic_count} funder(s)</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openParticipant(p.participant_id)}
                          data-testid={`network-funding-person-${p.participant_id}`}
                          className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                  {fundingPeople.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">
                      Nobody has a funding score yet. Scores appear once an attributed participant submits a funding connection.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* All connections */}
          {fundingView === 'connections' && (
            <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="network-funding-connections">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Funder</th>
                    <th className="text-left px-4 py-3 font-semibold">World</th>
                    <th className="text-left px-4 py-3 font-semibold">Submitted by</th>
                    <th className="text-left px-4 py-3 font-semibold">Depth</th>
                    <th className="text-left px-4 py-3 font-semibold">Willing to</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {fundingConns.map((c) => (
                    <tr key={c.connection_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{c.organisation}</div>
                        <div className="text-xs text-slate-500">{c.funder_type || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs">{c.funding_world}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-700">{c.submitter_display || c.submitter_email}</div>
                        {c.unmatched && <div className="mt-1"><Pill tone="amber">Unmatched</Pill></div>}
                      </td>
                      <td className="px-4 py-3">
                        <Pill tone={c.access_depth_score >= 3 ? 'green' : c.access_depth_score >= 2 ? 'amber' : 'slate'}>
                          {c.access_depth_score}/4
                        </Pill>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-700 max-w-xs">{c.willingness || '—'}</td>
                      <td className="px-4 py-3">
                        <Pill tone={c.status === 'Funded' ? 'green' : c.status === 'New' ? 'blue' : 'slate'}>{c.status}</Pill>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setReviewFunding({ ...c })}
                          data-testid={`network-open-funding-${c.connection_id}`}
                          className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                  {fundingConns.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">No funding connections match those filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {reviewFunding && (
        <Modal title={`Funding connection — ${reviewFunding.organisation}`}
          onClose={() => setReviewFunding(null)} testid="network-funding-modal" wide>
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <ReadOnly label="Funding world" value={reviewFunding.funding_world} />
              <ReadOnly label="Submitted by" value={`${reviewFunding.submitter_display || ''} <${reviewFunding.submitter_email}>`} />
              <ReadOnly label="Contact at the organisation" value={`${reviewFunding.contact_name || '—'}${reviewFunding.contact_role ? ` — ${reviewFunding.contact_role}` : ''}`} />
              <ReadOnly label="People they know there" value={reviewFunding.contacts_at_org} />
              <ReadOnly label="Typical ticket size" value={reviewFunding.typical_ticket} />
              <ReadOnly label="Regions" value={(reviewFunding.regions || []).join(', ')} />
              <ReadOnly label="Willing to" value={reviewFunding.willingness} />
              <ReadOnly label="Source" value={reviewFunding.source} />
            </div>
            <ReadOnly label="Nature of the relationship" value={reviewFunding.relationship_nature} />
            <ReadOnly label="Notes from the submitter" value={reviewFunding.notes} />

            <div className="grid sm:grid-cols-2 gap-6 border-t border-slate-200 pt-6">
              <Field label="Depth of access">
                <Select value={reviewFunding.access_depth}
                  onChange={(e) => setReviewFunding({ ...reviewFunding, access_depth: e.target.value })}
                  options={(fMeta.depths || []).map((d) => d.label)} placeholder="Not stated"
                  testid="network-funding-modal-depth" className="w-full" />
              </Field>
              <Field label="Status">
                <Select value={reviewFunding.status}
                  onChange={(e) => setReviewFunding({ ...reviewFunding, status: e.target.value })}
                  options={fMeta.statuses} testid="network-funding-modal-status" className="w-full" />
              </Field>
              <Field label="Priority">
                <Select value={reviewFunding.priority}
                  onChange={(e) => setReviewFunding({ ...reviewFunding, priority: e.target.value })}
                  options={fMeta.priorities} testid="network-funding-modal-priority" className="w-full" />
              </Field>
              <Field label="Assigned owner">
                <Input value={reviewFunding.assigned_owner || ''}
                  onChange={(e) => setReviewFunding({ ...reviewFunding, assigned_owner: e.target.value })}
                  data-testid="network-funding-modal-owner" />
              </Field>
            </div>

            <Field label="Internal notes">
              <Area rows={4} value={reviewFunding.admin_notes || ''}
                onChange={(e) => setReviewFunding({ ...reviewFunding, admin_notes: e.target.value })}
                data-testid="network-funding-modal-notes" />
            </Field>

            <label className="flex items-center gap-3 text-sm text-slate-700">
              <input type="checkbox" checked={!!reviewFunding.verified}
                onChange={(e) => setReviewFunding({ ...reviewFunding, verified: e.target.checked })}
                className="w-4 h-4 accent-slate-900" data-testid="network-funding-modal-verified" />
              Relationship confirmed by SAFE
            </label>

            <div className="flex gap-3">
              <button onClick={saveFundingConnection} disabled={busy === 'funding'}
                className="btn-primary" data-testid="network-funding-modal-save">
                {busy === 'funding' ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setReviewFunding(null)}
                className="text-sm font-medium px-5 py-3 border border-slate-300 hover:border-slate-900 transition-colors"
                data-testid="network-funding-modal-cancel">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---------------- Vetting ---------------- */}
      {sub === 'vetting' && (
        <div className="space-y-6" data-testid="network-vetting">
          <div className="border-l-2 border-rose-500 bg-rose-50 p-6">
            <p className="text-sm text-rose-900 leading-relaxed">
              <strong>Identity documents.</strong> Criminal history checks and photo ID are held in
              private storage, are never served publicly, and are readable only by Super Admins.
              <strong> Every time you open a document it is written to the audit log</strong> against
              your name. Do not download, forward or store these files anywhere else.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <Input placeholder="Search name or email…" value={vFilters.search}
              onChange={(e) => setVFilters({ ...vFilters, search: e.target.value })}
              data-testid="network-vetting-search" style={{ maxWidth: '20rem' }} />
            <Select value={vFilters.status}
              onChange={(e) => setVFilters({ ...vFilters, status: e.target.value })}
              options={vMeta.statuses} placeholder="All statuses" testid="network-vetting-status" />
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Person</th>
                  <th className="text-left px-4 py-3 font-semibold">Check issued</th>
                  <th className="text-left px-4 py-3 font-semibold">Photo ID</th>
                  <th className="text-left px-4 py-3 font-semibold">Documents</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vettingRecords.map((v) => (
                  <tr key={v.record_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{v.submitter_display || v.submitter_email}</div>
                      <div className="text-xs text-slate-500">{v.submitter_email}</div>
                      {v.unmatched && <div className="mt-1"><Pill tone="amber">Unmatched</Pill></div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-mono text-xs">{v.check_issued_on || '—'}</div>
                      <div className="text-xs text-slate-500">{v.check_country || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{v.id_document_type || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => openVettingDocument(v.record_id, 'police_check', 'Criminal history check')}
                          disabled={busy === 'doc-police_check'}
                          data-testid={`network-vetting-open-check-${v.record_id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-slate-300 hover:border-slate-900 transition-colors">
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
                          Police check
                        </button>
                        <button onClick={() => openVettingDocument(v.record_id, 'id_photo', 'Photo holding ID')}
                          disabled={busy === 'doc-id_photo'}
                          data-testid={`network-vetting-open-photo-${v.record_id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-slate-300 hover:border-slate-900 transition-colors">
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
                          ID photo
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={v.status === 'Verified' ? 'green'
                        : v.status === 'Rejected' ? 'red'
                          : v.status === 'Submitted' ? 'blue' : 'amber'}>{v.status}</Pill>
                      {v.reviewed_by && (
                        <div className="text-xs text-slate-500 mt-1">by {v.reviewed_by}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setReviewVetting({ ...v })}
                        data-testid={`network-vetting-review-${v.record_id}`}
                        className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
                {vettingRecords.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                    No police checks have been submitted yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {docPreview && (
        <Modal title={docPreview.label} onClose={closeDocPreview} testid="network-doc-preview" wide>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              This view has been recorded in the audit log. Close this window when you are done.
            </p>
            {docPreview.contentType === 'application/pdf' ? (
              <iframe src={docPreview.url} title={docPreview.label}
                className="w-full h-[70vh] border border-slate-200" data-testid="network-doc-pdf" />
            ) : (
              <img src={docPreview.url} alt={docPreview.label}
                className="max-w-full max-h-[70vh] mx-auto border border-slate-200"
                data-testid="network-doc-image" />
            )}
          </div>
        </Modal>
      )}

      {reviewVetting && (
        <Modal title={`Vetting — ${reviewVetting.submitter_display || reviewVetting.submitter_email}`}
          onClose={() => setReviewVetting(null)} testid="network-vetting-modal">
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <ReadOnly label="Email" value={reviewVetting.submitter_email} />
              <ReadOnly label="Submitted" value={(reviewVetting.created_at || '').slice(0, 10)} />
              <ReadOnly label="Check issued in" value={reviewVetting.check_country} />
              <ReadOnly label="Check issued on" value={reviewVetting.check_issued_on} />
              <ReadOnly label="Photo ID type" value={reviewVetting.id_document_type} />
              <ReadOnly label="Last reviewed by" value={reviewVetting.reviewed_by} />
            </div>
            <ReadOnly label="Notes from the submitter" value={reviewVetting.submitter_notes} />

            <div className="grid sm:grid-cols-2 gap-6 border-t border-slate-200 pt-6">
              <Field label="Verification status">
                <Select value={reviewVetting.status}
                  onChange={(e) => setReviewVetting({ ...reviewVetting, status: e.target.value })}
                  options={vMeta.statuses} testid="network-vetting-modal-status" className="w-full" />
              </Field>
            </div>
            <Field label="Internal notes">
              <Area rows={4} value={reviewVetting.admin_notes || ''}
                onChange={(e) => setReviewVetting({ ...reviewVetting, admin_notes: e.target.value })}
                data-testid="network-vetting-modal-notes" />
            </Field>

            <div className="flex gap-3">
              <button onClick={saveVetting} disabled={busy === 'vetting'} className="btn-primary"
                data-testid="network-vetting-modal-save">
                {busy === 'vetting' ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setReviewVetting(null)}
                className="text-sm font-medium px-5 py-3 border border-slate-300 hover:border-slate-900 transition-colors"
                data-testid="network-vetting-modal-cancel">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ---------------- Domain coverage ---------------- */}
      {sub === 'domains' && (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="network-domains">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Domain</th>
                <th className="text-left px-4 py-3 font-semibold">Council lead</th>
                <th className="text-center px-4 py-3 font-semibold">Council</th>
                <th className="text-center px-4 py-3 font-semibold">Advisors</th>
                <th className="text-center px-4 py-3 font-semibold">Experts</th>
                <th className="text-center px-4 py-3 font-semibold">Referrals</th>
                <th className="text-center px-4 py-3 font-semibold">Opps</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {coverage.map((row, i) => (
                <tr key={row.domain} className={row.under_supported ? 'bg-amber-50/60' : ''}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{row.domain}</div>
                    {row.under_supported && <div className="mt-1"><Pill tone="amber">Under-supported</Pill></div>}
                  </td>
                  <td className="px-4 py-3">
                    <Input value={row.council_lead}
                      onChange={(e) => setCoverage(coverage.map((c, idx) => idx === i ? { ...c, council_lead: e.target.value } : c))}
                      placeholder="Assign a lead" data-testid={`network-domain-lead-${i}`} />
                  </td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.council_members}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.advisors}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.experts}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.referrals}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{row.opportunities}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => saveDomainLead(row)} data-testid={`network-domain-save-${i}`}
                      className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">Save</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------------- Admins ---------------- */}
      {sub === 'admins' && (
        <div className="space-y-6" data-testid="network-admins">
          <div className="border-l-2 border-slate-900 bg-white p-6">
            <p className="text-sm text-slate-700 leading-relaxed">
              Admin accounts are created automatically the first time an <strong>@safestandard.ai</strong>
              {' '}Google account signs in. Everyone else is refused a session. Super Admin can only ever be
              held by an @safestandard.ai address, and that is enforced on the server. Keep the number
              of Super Admins small.
            </p>
          </div>
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Admin</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="text-left px-4 py-3 font-semibold">Last login</th>
                  <th className="text-left px-4 py-3 font-semibold">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {admins.map((a) => (
                  <tr key={a.user_id}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{a.name || '—'}</div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={a.role} onChange={(e) => updateAdminRole(a.user_id, { role: e.target.value })}
                        options={['ADMIN', 'SUPER_ADMIN']} testid={`network-admin-role-${a.user_id}`} />
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-600">{(a.last_login_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => updateAdminRole(a.user_id, { active: !a.active })}
                        data-testid={`network-admin-toggle-${a.user_id}`}
                        className="text-sm font-medium text-slate-900 underline underline-offset-4 hover:no-underline">
                        {a.active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------- Database migration ---------------- */}
      {sub === 'migration' && (
        <div className="space-y-6" data-testid="network-migration">
          <div className="border-l-2 border-slate-900 bg-white p-6">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Move to a dedicated database</h3>
            <p className="text-sm text-slate-600 max-w-3xl leading-relaxed">
              Copies every collection to a MongoDB Atlas cluster you own, preserving document IDs so
              running it twice can never duplicate or overwrite anything. Start with <strong>Dry
              run</strong>. Your connection string is used for the request and never stored, never
              logged and never written to the database — only the hostname appears in the audit log.
            </p>
            <p className="text-sm text-amber-800 mt-3 max-w-3xl leading-relaxed">
              Copying does not switch anything over. The app keeps using its current database until
              <strong> MONGO_URL</strong> and <strong>DB_NAME</strong> are changed in your production
              environment variables and you redeploy.
            </p>
          </div>

          <div className="bg-white border border-slate-200 p-6 space-y-5">
            <Field label="Atlas connection string">
              <input type="password" autoComplete="off" value={migration.target_uri}
                onChange={(e) => setMigration({ ...migration, target_uri: e.target.value })}
                placeholder="mongodb+srv://safe_app:PASSWORD@safe-production.xxxxx.mongodb.net/?retryWrites=true&w=majority"
                data-testid="migration-uri"
                className="w-full px-3 py-2 border border-slate-300 text-sm font-mono focus:outline-none focus:border-slate-900" />
            </Field>
            <Field label="Target database name">
              <Input value={migration.target_db} data-testid="migration-db"
                onChange={(e) => setMigration({ ...migration, target_db: e.target.value })} />
            </Field>
            <div className="flex flex-wrap gap-3 pt-2">
              <button onClick={() => runMigration('inspect')} disabled={busy.startsWith('migration')}
                className="btn-primary" data-testid="migration-inspect">
                {busy === 'migration-inspect' ? 'Connecting…' : '1. Dry run'}
              </button>
              <button onClick={() => runMigration('copy')} disabled={busy.startsWith('migration')}
                data-testid="migration-copy"
                className="text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                {busy === 'migration-copy' ? 'Copying…' : '2. Copy everything'}
              </button>
              <button onClick={() => runMigration('verify')} disabled={busy.startsWith('migration')}
                data-testid="migration-verify"
                className="text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                {busy === 'migration-verify' ? 'Checking…' : '3. Verify'}
              </button>
            </div>
          </div>

          {migrationResult && (
            <div className="bg-white border border-slate-200" data-testid="migration-result">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-bold text-slate-900">
                    {migrationResult.step === 'inspect' ? 'Dry run' : migrationResult.step === 'copy' ? 'Copy' : 'Verification'}
                    {' — '}{migrationResult.source_db || 'current database'} → {migrationResult.target_db}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {migrationResult.target_host}
                    {migrationResult.writable === false && ' · NOT WRITABLE'}
                    {migrationResult.all_match === true && ' · all collections match'}
                    {migrationResult.all_match === false && ' · shortfall detected'}
                  </p>
                </div>
                <button onClick={() => setMigrationResult(null)} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid="migration-result-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {migrationResult.write_error && (
                <div className="px-6 py-3 bg-rose-50 text-sm text-rose-800">{migrationResult.write_error}</div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold">Collection</th>
                    <th className="text-right px-6 py-3 font-semibold">
                      {migrationResult.step === 'copy' ? 'Copied' : 'Here'}
                    </th>
                    <th className="text-right px-6 py-3 font-semibold">
                      {migrationResult.step === 'copy' ? 'Already there' : 'Target'}
                    </th>
                    <th className="text-right px-6 py-3 font-semibold">
                      {migrationResult.step === 'copy' ? 'Result' : 'Status'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(migrationResult.collections || []).map((r) => (
                    <tr key={r.collection}>
                      <td className="px-6 py-3 font-mono text-xs text-slate-800">{r.collection}</td>
                      <td className="px-6 py-3 text-right text-slate-700">
                        {migrationResult.step === 'copy' ? r.copied : r.source}
                      </td>
                      <td className="px-6 py-3 text-right text-slate-700">
                        {migrationResult.step === 'copy' ? r.already_present : (r.target ?? '—')}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {migrationResult.step === 'copy' ? (
                          <Pill tone={r.copied ? 'green' : 'slate'}>
                            {r.copied ? 'Copied' : r.already_present ? 'Already there' : 'Empty'}
                          </Pill>
                        ) : migrationResult.step === 'verify' ? (
                          <Pill tone={r.match ? 'green' : r.volatile ? 'slate' : 'red'}>
                            {r.match ? (r.extra ? `Match (+${r.extra} extra)` : 'Match')
                              : r.volatile ? `${r.missing} behind (live log)` : `${r.missing} missing`}
                          </Pill>
                        ) : (
                          <span className="text-slate-600">{r.to_copy} to copy</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ---------------- Audit log ---------------- */}
      {sub === 'audit' && (
        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="network-audit">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Who</th>
                <th className="text-left px-4 py-3 font-semibold">Action</th>
                <th className="text-left px-4 py-3 font-semibold">Object</th>
                <th className="text-left px-4 py-3 font-semibold">Detail</th>
                <th className="text-left px-4 py-3 font-semibold">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {auditEntries.map((e) => (
                <tr key={e.entry_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-xs font-mono text-slate-600 whitespace-nowrap">{(e.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-3">
                    <div className="text-slate-800">{e.actor_email}</div>
                    <div className="text-xs text-slate-500">{e.actor_role}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Pill tone={e.action.includes('REJECTED') || e.action.includes('DENIED') ? 'red' : e.action === 'DATA_EXPORT' ? 'amber' : 'slate'}>
                      {e.action}
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{e.object_type}{e.object_id ? ` · ${e.object_id}` : ''}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-md">{e.detail}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-500">{e.ip}</td>
                </tr>
              ))}
              {auditEntries.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">No audit entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------------- Participant detail modal ---------------- */}
      {detail && detailForm && (
        <Modal title={`${detail.full_name || detail.email} — Council Snapshot & record`} onClose={() => setDetail(null)} testid="network-participant-modal" wide>
          <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full name"><Input value={detailForm.full_name} onChange={(e) => setDetailForm({ ...detailForm, full_name: e.target.value })} data-testid="network-detail-name" /></Field>
              <ReadOnly label="Email (attribution key)" value={detail.email} />
              <Field label="SAFE role">
                <Select className="w-full" value={detailForm.safe_role} onChange={(e) => setDetailForm({ ...detailForm, safe_role: e.target.value })}
                  options={pMeta.roles} testid="network-detail-role" />
              </Field>
              <Field label="Participant status">
                <Select className="w-full" value={detailForm.participant_status} onChange={(e) => setDetailForm({ ...detailForm, participant_status: e.target.value })}
                  options={pMeta.statuses} testid="network-detail-status" />
              </Field>
              <Field label="Job title"><Input value={detailForm.role_title} onChange={(e) => setDetailForm({ ...detailForm, role_title: e.target.value })} /></Field>
              <Field label="Organisation(s)"><Input value={detailForm.organisations} onChange={(e) => setDetailForm({ ...detailForm, organisations: e.target.value })} /></Field>
              <Field label="Country"><Input value={detailForm.country} onChange={(e) => setDetailForm({ ...detailForm, country: e.target.value })} /></Field>
              <Field label="Primary domain">
                <Select className="w-full" value={detailForm.primary_domain} onChange={(e) => setDetailForm({ ...detailForm, primary_domain: e.target.value })}
                  options={pMeta.domains} placeholder="Not set" testid="network-detail-domain" />
              </Field>
              <Field label="Priority">
                <Select className="w-full" value={detailForm.priority} onChange={(e) => setDetailForm({ ...detailForm, priority: e.target.value })}
                  options={pMeta.priorities} testid="network-detail-priority" />
              </Field>
              <Field label="SAFE relationship owner"><Input value={detailForm.assigned_owner} onChange={(e) => setDetailForm({ ...detailForm, assigned_owner: e.target.value })} /></Field>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-1">Council Snapshot</h4>
              <p className="text-xs text-slate-500 mb-4">
                The working document. Seeded from their answers — edit it into something the team can act on.
              </p>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  ['who_they_are', 'Who they are'],
                  ['what_they_are_building', "What they're building"],
                  ['what_safe_can_do_for_them', 'What SAFE can do for them'],
                  ['what_they_can_do_for_safe', 'What they can do for SAFE'],
                  ['key_relationships', 'Key relationships'],
                  ['potential_collaborations', 'Potential collaborations'],
                ].map(([key, label]) => (
                  <Field key={key} label={label}>
                    <Area rows={4} value={detailForm.snapshot[key]}
                      onChange={(e) => setDetailForm({ ...detailForm, snapshot: { ...detailForm.snapshot, [key]: e.target.value } })}
                      data-testid={`network-snapshot-${key}`} />
                  </Field>
                ))}
              </div>
              <div className="mt-5">
                <Field label="Follow-up actions">
                  <Area rows={3} value={detailForm.snapshot.follow_up_actions}
                    onChange={(e) => setDetailForm({ ...detailForm, snapshot: { ...detailForm.snapshot, follow_up_actions: e.target.value } })}
                    data-testid="network-snapshot-follow-up" />
                </Field>
              </div>
            </div>

            <Field label="Internal notes (never synced to HubSpot)">
              <Area rows={4} value={detailForm.internal_notes}
                onChange={(e) => setDetailForm({ ...detailForm, internal_notes: e.target.value })}
                data-testid="network-detail-notes" />
            </Field>

            {detail.profile && (
              <details className="border border-slate-200">
                <summary className="px-5 py-3 text-sm font-semibold text-slate-900 cursor-pointer bg-slate-50">
                  Full Partnership Profile answers
                </summary>
                <div className="p-5 grid md:grid-cols-2 gap-5">
                  <ReadOnly label="About them" value={detail.profile.about_you} />
                  <ReadOnly label="Most passionate about" value={detail.profile.passionate_about} />
                  <ReadOnly label="Next 12–24 months" value={detail.profile.next_12_24_months} />
                  <ReadOnly label="Expertise offered" value={detail.profile.expertise_offer} />
                  <ReadOnly label="Contribution areas" value={(detail.profile.contribution_areas || []).join(', ')} />
                  <ReadOnly label="Sectors with relationships" value={(detail.profile.sectors || []).join(', ')} />
                  <ReadOnly label="People SAFE should know" value={detail.profile.people_safe_should_know} />
                  <ReadOnly label="Happy to introduce SAFE" value={detail.profile.intro_willingness} />
                  <ReadOnly label="Opportunities to explore" value={detail.profile.opportunities_to_explore} />
                  <ReadOnly label="Joint initiatives" value={detail.profile.joint_initiatives} />
                  <ReadOnly label="How SAFE can help them" value={detail.profile.how_safe_can_help} />
                  <ReadOnly label="Council member to meet" value={detail.profile.council_member_to_meet} />
                  <ReadOnly label="Who else we should know" value={detail.profile.who_should_we_know} />
                  <ReadOnly label="Greatest contribution" value={detail.profile.greatest_contribution} />
                  <ReadOnly label="Anything else" value={detail.profile.anything_else} />
                </div>
              </details>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-3">Their referrals ({(detail.referrals || []).length})</h4>
                <div className="space-y-2">
                  {(detail.referrals || []).map((r) => (
                    <div key={r.referral_id} className="border border-slate-200 px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">{r.person_name}</div>
                      <div className="text-xs text-slate-500">{r.organisation || '—'} · {r.status}</div>
                    </div>
                  ))}
                  {(detail.referrals || []).length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-3">Their opportunities ({(detail.opportunities || []).length})</h4>
                <div className="space-y-2">
                  {(detail.opportunities || []).map((o) => (
                    <div key={o.opportunity_id} className="border border-slate-200 px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">{o.title}</div>
                      <div className="text-xs text-slate-500">{o.opportunity_type || '—'} · {o.status}</div>
                    </div>
                  ))}
                  {(detail.opportunities || []).length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-3">
                Funding access ({(detail.funding_connections || []).length} funder{(detail.funding_connections || []).length === 1 ? '' : 's'})
                {' — '}commercial {detail.funding_commercial_score ?? 0}/100 · philanthropic {detail.funding_philanthropic_score ?? 0}/100
              </h4>
              <div className="grid md:grid-cols-2 gap-2">
                {(detail.funding_connections || []).map((f) => (
                  <div key={f.connection_id} className="border border-slate-200 px-4 py-3 text-sm">
                    <div className="font-semibold text-slate-900">{f.organisation}</div>
                    <div className="text-xs text-slate-500">
                      {f.funding_world} · depth {f.access_depth_score}/4
                      {f.contact_name ? ` · ${f.contact_name}` : ''}
                    </div>
                  </div>
                ))}
                {(detail.funding_connections || []).length === 0 && (
                  <p className="text-sm text-slate-500">No funding connections submitted.</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200">
              <button onClick={saveParticipant} disabled={busy === 'participant'} className="btn-primary" data-testid="network-detail-save">
                {busy === 'participant' ? 'Saving…' : 'Save changes'}
              </button>
              <button onClick={() => syncHubspot(detail.participant_id)} disabled={busy === 'hubspot'}
                data-testid="network-detail-hubspot"
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                <ExternalLink className="w-4 h-4" strokeWidth={1.75} />
                {busy === 'hubspot' ? 'Syncing…' : 'Sync approved fields to HubSpot'}
              </button>
              {detail.hubspot_contact_id && (
                <span className="text-xs text-slate-500">HubSpot contact {detail.hubspot_contact_id}</span>
              )}
              {detailForm.unmatched && (
                <button onClick={() => setDetailForm({ ...detailForm, unmatched: false })}
                  data-testid="network-detail-attribute"
                  className="text-sm font-medium px-4 py-2.5 border border-amber-400 text-amber-800 hover:border-amber-600 transition-colors">
                  Mark as attributed
                </button>
              )}
              {isSuper && (
                <button onClick={deleteParticipant} disabled={busy === 'delete'}
                  data-testid="network-detail-delete"
                  className="ml-auto text-sm font-medium px-4 py-2.5 border border-rose-300 text-rose-700 hover:border-rose-600 transition-colors">
                  {busy === 'delete' ? 'Removing…' : 'Remove participant'}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ---------------- Referral review modal ---------------- */}
      {reviewReferral && (
        <Modal title={`Referral — ${reviewReferral.person_name}`} onClose={() => setReviewReferral(null)} testid="network-referral-modal">
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-5">
              <ReadOnly label="Referred by" value={`${reviewReferral.referrer_display || ''} (${reviewReferral.referrer_email})`} />
              <ReadOnly label="Submitted" value={(reviewReferral.created_at || '').slice(0, 19).replace('T', ' ')} />
              <ReadOnly label="Professional title" value={reviewReferral.person_title} />
              <ReadOnly label="Organisation" value={reviewReferral.organisation} />
              <ReadOnly label="Organisation website" value={reviewReferral.organisation_website} />
              <ReadOnly label="LinkedIn" value={reviewReferral.linkedin} />
              <ReadOnly label="Email provided" value={reviewReferral.person_email} />
              <ReadOnly label="Country" value={reviewReferral.country} />
              <ReadOnly label="Sectors" value={(reviewReferral.sectors || []).join(', ')} />
              <ReadOnly label="Introduction available" value={reviewReferral.introduction_available} />
            </div>
            <ReadOnly label="How they know them" value={reviewReferral.how_you_know_them} />
            <ReadOnly label="Why valuable to SAFE" value={reviewReferral.why_valuable} />

            <div className="grid md:grid-cols-3 gap-5 pt-4 border-t border-slate-200">
              <Field label="Status">
                <Select className="w-full" value={reviewReferral.status} onChange={(e) => setReviewReferral({ ...reviewReferral, status: e.target.value })}
                  options={rMeta.statuses} testid="network-referral-status-select" />
              </Field>
              <Field label="Priority">
                <Select className="w-full" value={reviewReferral.priority} onChange={(e) => setReviewReferral({ ...reviewReferral, priority: e.target.value })}
                  options={rMeta.priorities} testid="network-referral-priority-select" />
              </Field>
              <Field label="Domain">
                <Select className="w-full" value={reviewReferral.domain || ''} onChange={(e) => setReviewReferral({ ...reviewReferral, domain: e.target.value })}
                  options={rMeta.domains} placeholder="Not set" testid="network-referral-domain-select" />
              </Field>
            </div>
            <Field label="Owner"><Input value={reviewReferral.assigned_owner || ''} onChange={(e) => setReviewReferral({ ...reviewReferral, assigned_owner: e.target.value })} /></Field>
            <Field label="Internal notes">
              <Area rows={4} value={reviewReferral.admin_notes || ''} onChange={(e) => setReviewReferral({ ...reviewReferral, admin_notes: e.target.value })} data-testid="network-referral-notes" />
            </Field>

            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200">
              <button onClick={saveReferral} disabled={busy === 'referral'} className="btn-primary" data-testid="network-referral-save">
                {busy === 'referral' ? 'Saving…' : 'Save referral'}
              </button>
              <button onClick={promoteReferral} disabled={busy === 'promote'} data-testid="network-referral-promote"
                className="text-sm font-medium px-4 py-2.5 border border-slate-300 hover:border-slate-900 transition-colors">
                {busy === 'promote' ? 'Creating…' : 'Create HubSpot contact'}
              </button>
              <span className="text-xs text-slate-500">
                Referrals only reach HubSpot once you have approved engagement.
              </span>
            </div>
          </div>
        </Modal>
      )}

      {/* ---------------- Opportunity review modal ---------------- */}
      {reviewOpportunity && (
        <Modal title={`Opportunity — ${reviewOpportunity.title}`} onClose={() => setReviewOpportunity(null)} testid="network-opportunity-modal">
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-5">
              <ReadOnly label="Submitted by" value={`${reviewOpportunity.submitter_display || ''} (${reviewOpportunity.submitter_email})`} />
              <ReadOnly label="Submitted" value={(reviewOpportunity.created_at || '').slice(0, 19).replace('T', ' ')} />
              <ReadOnly label="Organisation" value={reviewOpportunity.organisation} />
              <ReadOnly label="Country" value={reviewOpportunity.country} />
            </div>
            <ReadOnly label="Description" value={reviewOpportunity.description} />
            <ReadOnly label="Why it matters" value={reviewOpportunity.why_it_matters} />
            <ReadOnly label="Support needed from SAFE" value={reviewOpportunity.support_needed} />

            <div className="grid md:grid-cols-4 gap-5 pt-4 border-t border-slate-200">
              <Field label="Status">
                <Select className="w-full" value={reviewOpportunity.status} onChange={(e) => setReviewOpportunity({ ...reviewOpportunity, status: e.target.value })}
                  options={oMeta.statuses} testid="network-opportunity-status-select" />
              </Field>
              <Field label="Priority">
                <Select className="w-full" value={reviewOpportunity.priority} onChange={(e) => setReviewOpportunity({ ...reviewOpportunity, priority: e.target.value })}
                  options={oMeta.priorities} testid="network-opportunity-priority-select" />
              </Field>
              <Field label="Type">
                <Select className="w-full" value={reviewOpportunity.opportunity_type || ''} onChange={(e) => setReviewOpportunity({ ...reviewOpportunity, opportunity_type: e.target.value })}
                  options={oMeta.types} placeholder="Not set" />
              </Field>
              <Field label="Deadline">
                <Input type="date" value={(reviewOpportunity.deadline || '').slice(0, 10)}
                  onChange={(e) => setReviewOpportunity({ ...reviewOpportunity, deadline: e.target.value })} data-testid="network-opportunity-deadline" />
              </Field>
            </div>
            <Field label="Owner"><Input value={reviewOpportunity.assigned_owner || ''} onChange={(e) => setReviewOpportunity({ ...reviewOpportunity, assigned_owner: e.target.value })} /></Field>
            <Field label="Internal notes">
              <Area rows={4} value={reviewOpportunity.admin_notes || ''} onChange={(e) => setReviewOpportunity({ ...reviewOpportunity, admin_notes: e.target.value })} data-testid="network-opportunity-notes" />
            </Field>

            <button onClick={saveOpportunity} disabled={busy === 'opportunity'} className="btn-primary" data-testid="network-opportunity-save">
              {busy === 'opportunity' ? 'Saving…' : 'Save opportunity'}
            </button>
          </div>
        </Modal>
      )}

      {/* ---------------- New participant modal ---------------- */}
      {newParticipant && (
        <Modal title="Add a participant" onClose={() => setNewParticipant(null)} testid="network-new-participant-modal">
          <div className="space-y-5">
            <Field label="Full name"><Input value={newParticipant.full_name} onChange={(e) => setNewParticipant({ ...newParticipant, full_name: e.target.value })} data-testid="network-new-name" /></Field>
            <Field label="Email (their attribution key)"><Input type="email" value={newParticipant.email} onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })} data-testid="network-new-email" /></Field>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="SAFE role">
                <Select className="w-full" value={newParticipant.safe_role} onChange={(e) => setNewParticipant({ ...newParticipant, safe_role: e.target.value })}
                  options={pMeta.roles} testid="network-new-role" />
              </Field>
              <Field label="Primary domain">
                <Select className="w-full" value={newParticipant.primary_domain} onChange={(e) => setNewParticipant({ ...newParticipant, primary_domain: e.target.value })}
                  options={pMeta.domains} placeholder="Not set" />
              </Field>
              <Field label="Job title"><Input value={newParticipant.role_title} onChange={(e) => setNewParticipant({ ...newParticipant, role_title: e.target.value })} /></Field>
              <Field label="Organisation"><Input value={newParticipant.organisations} onChange={(e) => setNewParticipant({ ...newParticipant, organisations: e.target.value })} /></Field>
              <Field label="Country"><Input value={newParticipant.country} onChange={(e) => setNewParticipant({ ...newParticipant, country: e.target.value })} /></Field>
            </div>
            <button onClick={createParticipant} disabled={busy === 'create' || !newParticipant.email || !newParticipant.full_name}
              className="btn-primary" data-testid="network-new-save">
              {busy === 'create' ? 'Adding…' : 'Add participant'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default NetworkAdmin;
